import React, { useReducer, useEffect, useCallback, useMemo, useState } from 'react'
import CustomForm from './CustomForm'
import { hasInstitutionalGroup, useAuth } from '../../utils/context/AuthContext'
import { startOauthFlow } from '../../utils/oauth-init'
import { useNavigationHistory } from '../../utils/context/NavigationHistoryContext'
import { useLocation } from '@gatsbyjs/reach-router'
import { assertNoAuthErrorPayload, extractAuthErrorDetails } from '../../utils/auth-errors'
import {
  clearStoredDestinations,
  resolvePostLoginDestination,
  sanitizeRedirectTarget,
  storeOauthReturnPath,
  peekLoginSource,
  storeLoginSource,
  buildConfirmEmailPath
} from '../../utils/auth-redirect'
import { clearStoredConfirmEmailState } from '../../utils/confirm-email-storage'
import { navigate } from 'gatsby'
import { ReactComponent as HarborLogo } from '../../images/HFLogoOnlyDark.svg'
import { ReactComponent as GoogleLogo } from '../../assets/images/icons/icons8-google.svg'
import { ReactComponent as AppleLogo } from '../../assets/images/logos/Apple_logo_black.svg'
import { ReactComponent as LinkedInLogo } from '../../assets/images/logos/LinkedIn_icon.svg'
import * as styles from '../../styles/components/cognito-login-form.module.scss'
import { useAnalytics } from '../../utils/analytics/useAnalytics'
import { setLoginMethod } from '../../utils/analytics/dataLayer'

const cx = (...x) => x.filter(Boolean).join(' ')
const init = { email: '', password: '' }
const red = (s, a) => ({ ...s, ...a })

const LOGIN_TYPE_LABELS = {
  email_password: 'Email & Password',
  google: 'Google',
  linkedin: 'LinkedIn',
  apple: 'Apple'
}

const DEFAULT_ERROR_MESSAGE = 'Login failed. Please try again.'
const INVALID_CREDENTIALS_MESSAGE = 'Invalid Email or Password.'
const LOGIN_HINT_STORAGE_KEY = 'cognito-login-hint'

const clearStoredLoginHint = () => {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(LOGIN_HINT_STORAGE_KEY)
  } catch {
    /* no-op */
  }
}

const storeLoginHint = (message, email) => {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(
      LOGIN_HINT_STORAGE_KEY,
      JSON.stringify({ message, email: email || '' })
    )
  } catch {
    /* no-op */
  }
}

const formatLoginType = (type) => {
  if (!type) return null
  const key = `${type}`.toLowerCase()
  return LOGIN_TYPE_LABELS[key] || null
}

const normalizeEmail = (value) => (typeof value === 'string' ? value.trim().toLowerCase() : '')

const buildHintDetails = (hint, attemptedEmail) => {
  if (!hint || typeof hint !== 'object') return null
  const status = typeof hint.status === 'string' ? hint.status.toLowerCase() : ''
  const loginLabel = formatLoginType(hint.correct_login_type)
  const primaryEmail = typeof hint.primary_email === 'string' ? hint.primary_email.trim() : ''
  const attempted = normalizeEmail(attemptedEmail)
  const primaryMatchesAttempt = primaryEmail && normalizeEmail(primaryEmail) === attempted
  const details = { message: null, recommendedEmail: null, fromHint: true }

  switch (status) {
    case 'verification_email_used':
      if (primaryEmail && !primaryMatchesAttempt) {
        details.message = `The email you entered is only used for verification. Sign in using your primary email ${primaryEmail}.`
        details.recommendedEmail = primaryEmail
        return details
      }
      details.message = 'The email you entered is only used for verification. Please enter your primary email address to sign in.'
      return details
    case 'verification_email_used_social':
      if (loginLabel && primaryEmail && !primaryMatchesAttempt) {
        details.message = `The email you entered is only used for verification on your ${loginLabel} login. Select "Sign in with ${loginLabel}" above and continue with ${primaryEmail}.`
        details.recommendedEmail = primaryEmail
        return details
      }
      if (loginLabel) {
        details.message = `The email you entered is only used for verification on your ${loginLabel} login. Please select "Sign in with ${loginLabel}" above.`
        return details
      }
      details.message = 'The email you entered is only used for verification on your social login. Please use the matching social sign-in option above.'
      return details
    case 'social_login_email_used':
      if (loginLabel && primaryEmail && !primaryMatchesAttempt) {
        details.message = `The email you entered is linked to your ${loginLabel} sign-in (${primaryEmail}). Please select "Sign in with ${loginLabel}" above to continue.`
        details.recommendedEmail = primaryEmail
        return details
      }
      if (loginLabel) {
        details.message = `The email you entered is linked to your ${loginLabel} sign-in. Please select "Sign in with ${loginLabel}" above to continue.`
        return details
      }
      details.message = 'The email you entered is linked to a social sign-in. Please use the matching social sign-in option above.'
      return details
    case 'invalid_login':
      details.message = 'We could not find an account with that email. Double-check the address or choose Sign up to create one.'
      return details
    default:
      if (loginLabel) {
        details.message = `We found that this email is associated with your ${loginLabel} sign-in. Please use the "Sign in with ${loginLabel}" option above.`
        if (primaryEmail && !primaryMatchesAttempt) {
          details.recommendedEmail = primaryEmail
        }
        return details
      }
  }

  return null
}

const extractHintPayload = (error) => {
  const body = error?.body
  if (!body || typeof body !== 'object') return null
  if (body?.hint && typeof body.hint === 'object') return body.hint
  const hintLikeKeys = ['status', 'correct_login_type', 'primary_email', 'ok']
  const looksLikeHint = hintLikeKeys.some((key) => Object.prototype.hasOwnProperty.call(body, key))
  return looksLikeHint ? body : null
}

const resolveLoginError = (error, attemptedEmail) => {
  const hint = extractHintPayload(error)
  const hintDetails = buildHintDetails(hint, attemptedEmail)
  if (hintDetails?.message) return hintDetails

  const parsedError = extractAuthErrorDetails(error, DEFAULT_ERROR_MESSAGE)
  const topLevelName = typeof parsedError?.code === 'string' ? parsedError.code.toLowerCase() : ''
  const errorName = typeof error?.body?.name === 'string' ? error.body.name.toLowerCase() : topLevelName
  const errorMessage = typeof parsedError?.message === 'string' ? parsedError.message : ''
  const bodyError = typeof error?.body?.error === 'string' ? error.body.error : ''
  const bodyMessage = typeof error?.body?.message === 'string' ? error.body.message : ''

  if (errorName === 'notauthorizedexception' || errorName === 'usernotfoundexception') {
    return { message: INVALID_CREDENTIALS_MESSAGE, recommendedEmail: null, fromHint: false }
  }

  if (
    /incorrect username or password/i.test(errorMessage) ||
    /incorrect username or password/i.test(bodyError) ||
    /incorrect username or password/i.test(bodyMessage)
  ) {
    return { message: INVALID_CREDENTIALS_MESSAGE, recommendedEmail: null, fromHint: false }
  }

  if (errorMessage && errorMessage !== DEFAULT_ERROR_MESSAGE) {
    return { message: errorMessage, recommendedEmail: null, fromHint: false }
  }

  return { message: DEFAULT_ERROR_MESSAGE, recommendedEmail: null, fromHint: false }
}

const getLoginErrorCategory = (error) => {
  const name = `${error?.body?.name || error?.name || ''}`.toLowerCase()
  if (name.includes('notauthorized') || name.includes('usernotfound')) return 'invalid_credentials'
  if (name.includes('timeout')) return 'timeout'
  if (name.includes('network')) return 'network'
  return 'unknown'
}

function OrDivider({ children = 'OR SIGN IN WITH EMAIL' }) {
  return (
    <div className={styles?.orDivider || ''}>
      <span>{children}</span>
    </div>
  )
}

export default function CognitoLoginForm({ onSuccess }) {
  const [state, set] = useReducer(red, init)
  const { login, user, loading } = useAuth()
  const [loginHint, setLoginHint] = useState('')
  const location = useLocation()
  const { getPreviousLocation } = useNavigationHistory()
  const { track } = useAnalytics({
    component: { name: 'CognitoLoginForm', description: 'Login form' },
  })
  const isLoggedIn = useMemo(() => Boolean(user), [user])
  const primaryButtonStyles = useMemo(() => ({
    width: '100%',
    padding: '12px 16px',
    fontSize: '16px',
    fontWeight: '600',
    borderRadius: '16px',
    marginTop: '0px',
    marginBottom: '0px'
  }), [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      const raw = window.sessionStorage.getItem(LOGIN_HINT_STORAGE_KEY)
      if (!raw) return
      window.sessionStorage.removeItem(LOGIN_HINT_STORAGE_KEY)
      const parsed = JSON.parse(raw)
      if (parsed && typeof parsed === 'object') {
        if (parsed.message) setLoginHint(String(parsed.message))
        if (parsed.email) set({ email: parsed.email })
      }
    } catch {
      clearStoredLoginHint()
    }
  }, [set])

  const fields = useMemo(
    () => [
      { type: 'input', props: { id: 'email', name: 'email', label: 'Email', type: 'email', freevalidation: 'false', required: true, autoComplete: 'email' } },
      { type: 'input', props: { id: 'password', name: 'password', label: 'Password', type: 'password', required: true, autoComplete: 'current-password' } }
    ],
    []
  )

  const resolveDestination = useCallback(
    (options = {}) => resolvePostLoginDestination({
      search: location?.search,
      state: location?.state,
      previousLocation: getPreviousLocation(),
      ...options
    }),
    [location, getPreviousLocation]
  )

  const pruneLoginParam = useCallback(() => {
    if (typeof window === 'undefined') return
    const current = new URL(window.location.href)
    if (current.searchParams.get('form') === 'login') {
      current.searchParams.delete('form')
      navigate(`${current.pathname}${current.search}${current.hash || ''}`, { replace: true })
    }
  }, [])

  const submit = useCallback(async v => {
    const attemptedEmail = typeof v?.email === 'string' ? v.email : ''
    const attemptedPassword = typeof v?.password === 'string' ? v.password : ''
    const loginMethod = 'email_password'
    setLoginMethod(loginMethod)
    track('login_attempt', {
      interaction: { type: 'submit' },
      user: { loginMethod },
    })
    clearStoredLoginHint()
    set({ email: attemptedEmail, password: attemptedPassword })
    setLoginHint('')
    try {
      const loggedInUser = await login(v.email, v.password)
      assertNoAuthErrorPayload(loggedInUser, DEFAULT_ERROR_MESSAGE)
      track('login_success', {
        interaction: { type: 'success' },
        user: { loginMethod },
      })
      const destination = resolveDestination()
      const sanitizedDestination = sanitizeRedirectTarget(destination)
      const loginSource = peekLoginSource()
      onSuccess?.({ skipRedirect: true, preserveLoginSource: true })
      setLoginHint('')
      clearStoredLoginHint()

      const hasInstitutionalAccess = hasInstitutionalGroup(loggedInUser)

      if (typeof window !== 'undefined') {
        const currentPath = sanitizeRedirectTarget(
          `${window.location.pathname}${window.location.search || ''}${window.location.hash || ''}`
        )
        const sanitizedSource = sanitizeRedirectTarget(loginSource)
        const sourceMatchesDestination = sanitizedSource && sanitizedDestination && sanitizedSource === sanitizedDestination
        const sourceTarget = sourceMatchesDestination ? null : sanitizedSource
        const confirmRedirectCandidate = [sanitizedDestination, sourceTarget].find((candidate) => candidate && !candidate.startsWith('/confirm_email') && !candidate.includes('/institutional/login')) || '/'

        clearStoredDestinations()

        let redirected = false
        let thanksMessage = 'You are logged in.'

        if (hasInstitutionalAccess && sanitizedDestination && (!currentPath || sanitizedDestination !== currentPath)) {
          navigate(sanitizedDestination, { replace: true })
          redirected = true
          thanksMessage = 'You are logged in. Redirecting…'
        } else if (!hasInstitutionalAccess) {
          const confirmTarget = buildConfirmEmailPath(loggedInUser?.sub, confirmRedirectCandidate)
          if (!currentPath || confirmTarget !== currentPath) {
            navigate(confirmTarget, { replace: true })
            redirected = true
            thanksMessage = 'Please confirm your business email to continue.'
          }
        }

        if (!redirected) pruneLoginParam()

        return { thanksMessage }
      }

      return { thanksMessage: 'You are logged in.' }
    } catch (error) {
      track('login_error', {
        interaction: { type: 'submit' },
        user: { loginMethod },
        content: { errorCategory: getLoginErrorCategory(error) },
      })
      const { message, recommendedEmail, fromHint } = resolveLoginError(error, v?.email || '')
      const fallbackMessage = message || DEFAULT_ERROR_MESSAGE
      const nextEmail = recommendedEmail || attemptedEmail
      set({ email: nextEmail, password: attemptedPassword })
      setLoginHint(fallbackMessage)
      storeLoginHint(fallbackMessage, nextEmail)
      return { errorMessage: fallbackMessage, fromHint }
    }
  }, [login, onSuccess, resolveDestination, pruneLoginParam, set, track])

  const guardWhileLoading = useCallback((fn) => {
    if (loading) return
    fn()
  }, [loading])

  const rememberOAuthReturn = useCallback(() => {
    if (typeof window === 'undefined') return
    if (!peekLoginSource()) {
      storeLoginSource({
        pathname: window.location.pathname || '',
        search: window.location.search || '',
        hash: window.location.hash || ''
      })
    }
    const target = resolveDestination({ fallbackToCurrent: true, includeStoredOauth: false }) || '/institutional/login/'
    storeOauthReturnPath(target)
  }, [resolveDestination])

  const handleOAuth = useCallback((provider) => {
    guardWhileLoading(() => {
      const providerMap = { Google: 'google', LinkedIn: 'linkedin', SignInWithApple: 'apple' }
      const loginMethod = providerMap[provider] || `${provider}`.toLowerCase()
      setLoginMethod(loginMethod)
      track('oauth_login_start', {
        interaction: { type: 'click', text: `Sign in with ${provider}` },
        user: { loginMethod },
      })
      clearStoredConfirmEmailState()
      rememberOAuthReturn()
      startOauthFlow(provider)
    })
  }, [guardWhileLoading, rememberOAuthReturn, track])

  const handleGoogleClick = useCallback(() => handleOAuth('Google'), [handleOAuth])
  const handleLinkedInClick = useCallback(() => handleOAuth('LinkedIn'), [handleOAuth])
  const handleAppleClick = useCallback(() => handleOAuth('SignInWithApple'), [handleOAuth])
  const handleRegisterClick = useCallback(() => {
    if (typeof window === 'undefined') return
    track('sign_up_click', {
      interaction: { type: 'click', text: 'Sign up', linkUrl: '/institutional/register/' },
    })
    window.location.href = '/institutional/register/'
  }, [track])
  const handleSignOut = useCallback(() => {
    if (typeof window === 'undefined') return
    track('logout_click', {
      interaction: { type: 'click', text: 'Sign out', linkUrl: '/logout/' },
    })
    window.location.href = '/logout/'
  }, [track])

  return (
    <section className={styles?.root || ''} aria-busy={loading}>
      <div className={styles?.center || ''}>
        <HarborLogo title="Harbor Capital Logo" width={150} height={58} className={styles?.logo || ''} />
      </div>

      {!isLoggedIn && (
        <>
          <div className={cx(styles?.panel || '', styles?.social || '')}>
            <button className={cx(styles?.btn || '', styles?.btnOutlined || '', styles?.btnFull || '', styles?.btnGap || '')} onClick={handleGoogleClick} disabled={loading} aria-disabled={loading}>
              <GoogleLogo title="Google Logo" height={24} width={24} />
              <span className={styles?.btnLabelStrong || ''}>Sign in with Google</span>
            </button>
            <button className={cx(styles?.btn || '', styles?.btnOutlined || '', styles?.btnFull || '', styles?.btnGap || '')} onClick={handleLinkedInClick} disabled={loading} aria-disabled={loading}>
              <LinkedInLogo title="LinkedIn Logo" height={24} width={24} />
              <span className={styles?.btnLabelStrong || ''}>Sign in with LinkedIn</span>
            </button>
            <button className={cx(styles?.btn || '', styles?.btnOutlined || '', styles?.btnFull || '', styles?.btnGap || '')} onClick={handleAppleClick} disabled={loading} aria-disabled={loading}>
              <AppleLogo title="Apple Logo" height={24} width={24} />
              <span className={styles?.btnLabelStrong || ''}>Sign in with Apple</span>
            </button>
            <OrDivider />
          </div>

          <div className={styles?.panel || ''}>
            <div className={styles?.emailForm || ''}>
              {loginHint && (
                <div className={styles?.alert || ''} role="alert" aria-live="assertive">
                  {loginHint}
                </div>
              )}
              <CustomForm
                formId="cognito-login"
                formFields={fields}
                initialValues={state}
                onSubmit={submit}
                onValuesChange={set}
                ctaText="Sign in"
                hideRecaptcha={true}
                buttonStyles={primaryButtonStyles}
                submitDisabled={loading}
                suppressErrorDisplay={Boolean(loginHint)}
                requireCsrf={false}
              />
            </div>
          </div>

          <div className={styles?.panel || ''}>
            <div className={styles?.centerRow || ''}>
              <p className={styles?.muted || ''}>Not registered yet?</p>
              <button className={cx(styles?.btn || '', styles?.btnText || '')} onClick={handleRegisterClick} disabled={loading} aria-disabled={loading}>Sign up</button>
            </div>
          </div>
        </>
      )}

      {isLoggedIn && (
        <div className={styles?.panel || ''}>
          <div className={styles?.center || ''}>
            <p className={styles?.muted || ''}>You are logged in.</p>
            <button className={cx(styles?.btn || '', styles?.btnText || '')} onClick={handleSignOut}>Sign out</button>
          </div>
        </div>
      )}
    </section>
  )
}
